/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
// Timeline Administration API Functions
import { apiClient } from '../api';
import { BackingTrack, GenerateTimelineRequest } from '../types/jamTrack';

// Mock data for development
const MOCK_TIMELINES: BackingTrack[] = [
  {
    id: 'demo-1',
    youTubeVideoId: 'dQw4w9WgXcQ',
    youTubeUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    title: 'Rock Pop Backing Track - D Dorian',
    description: 'Sample rock backing track for demonstration',
    genre: 'Rock',
    bpm: 110,
    timeline: [
      { chordInstance: 1, startTime: 0, endTime: 4, chord: 'Dm' },
      { chordInstance: 2, startTime: 4, endTime: 8, chord: 'Am' },
      { chordInstance: 3, startTime: 8, endTime: 12, chord: 'C' },
      { chordInstance: 4, startTime: 12, endTime: 16, chord: 'G' },
    ],
    supportedScales: [
      { key: 'D', scaleType: 'Dorian', confidence: 0.95 },
      { key: 'C', scaleType: 'Major', confidence: 0.87 }
    ],
    detectionConfidence: 0.91,
    status: 'completed',
    createdAt: '2024-01-15T10:30:00Z'
  },
  {
    id: 'demo-2',
    youTubeVideoId: 'xyz123',
    youTubeUrl: 'https://www.youtube.com/watch?v=xyz123',
    title: 'Blues Progression in A',
    description: 'Classic 12-bar blues progression',
    genre: 'Blues',
    bpm: 85,
    timeline: [
      { chordInstance: 1, startTime: 0, endTime: 8, chord: 'A7' },
      { chordInstance: 2, startTime: 8, endTime: 16, chord: 'A7' },
      { chordInstance: 3, startTime: 16, endTime: 20, chord: 'D7' },
      { chordInstance: 4, startTime: 20, endTime: 24, chord: 'A7' },
      { chordInstance: 5, startTime: 24, endTime: 26, chord: 'E7' },
      { chordInstance: 6, startTime: 26, endTime: 28, chord: 'D7' },
      { chordInstance: 7, startTime: 28, endTime: 32, chord: 'A7' },
    ],
    supportedScales: [
      { key: 'A', scaleType: 'Mixolydian', confidence: 0.92 },
      { key: 'A', scaleType: 'Blues', confidence: 0.88 }
    ],
    detectionConfidence: 0.89,
    status: 'completed',
    createdAt: '2024-01-14T15:45:00Z'
  },
  {
    id: 'demo-3',
    youTubeVideoId: 'processing123',
    youTubeUrl: 'https://www.youtube.com/watch?v=processing123',
    title: 'Processing Timeline...',
    description: 'Currently being analyzed',
    genre: 'Pop',
    bpm: 120,
    timeline: [],
    supportedScales: [],
    detectionConfidence: undefined,
    status: 'processing',
    createdAt: '2024-01-16T09:15:00Z'
  }
];

// Get all timelines (admin only)
export const fetchAllTimelines = async (): Promise<BackingTrack[]> => {
  try {
    // Try to fetch from API first
    const response = await apiClient.get('/api/timeline/all');
    return response.data;
  } catch (error) {
    console.warn('API not available, using mock data:', error);
    // Return mock data if API is not available
    return new Promise((resolve) => {
      setTimeout(() => resolve(MOCK_TIMELINES), 500);
    });
  }
};

// Get timeline by ID  
export const fetchTimelineById = async (id: string): Promise<BackingTrack> => {
  try {
    const response = await apiClient.get(`/api/timeline/${id}`);
    return response.data;
  } catch (error) {
    console.warn('API not available, using mock data for timeline ID:', id);
    const timeline = MOCK_TIMELINES.find(t => t.id === id);
    if (!timeline) throw new Error('Timeline not found');
    return Promise.resolve(timeline);
  }
};

// Generate a new timeline
export const generateTimeline = async (request: GenerateTimelineRequest): Promise<BackingTrack> => {
  try {
    const response = await apiClient.post('/api/timeline/generate', request);
    return response.data;
  } catch (error) {
    console.warn('API not available, creating mock timeline for:', request.youTubeUrl);
    // Extract video ID from URL
    const videoId = request.youTubeUrl.split('v=')[1]?.split('&')[0] || 'mock-id';
    
    const newTimeline: BackingTrack = {
      id: `mock-${Date.now()}`,
      youTubeVideoId: videoId,
      youTubeUrl: request.youTubeUrl,
      title: `Generated Timeline - ${request.key || 'Unknown'} ${request.scale || 'Scale'}`,
      description: 'Mock generated timeline for testing',
      genre: request.genre || 'Rock',
      bpm: request.bpm || 120,
      timeline: [
        { chordInstance: 1, startTime: request.startOffsetSeconds || 0, endTime: (request.startOffsetSeconds || 0) + 4, chord: request.expectedChords?.[0] || 'C' },
        { chordInstance: 2, startTime: (request.startOffsetSeconds || 0) + 4, endTime: (request.startOffsetSeconds || 0) + 8, chord: request.expectedChords?.[1] || 'Am' },
        { chordInstance: 3, startTime: (request.startOffsetSeconds || 0) + 8, endTime: (request.startOffsetSeconds || 0) + 12, chord: request.expectedChords?.[2] || 'F' },
        { chordInstance: 4, startTime: (request.startOffsetSeconds || 0) + 12, endTime: (request.startOffsetSeconds || 0) + 16, chord: request.expectedChords?.[3] || 'G' },
      ],
      supportedScales: [
        { key: request.key || 'C', scaleType: request.scale || 'Major', confidence: 0.85 }
      ],
      detectionConfidence: 0.85,
      status: 'completed',
      createdAt: new Date().toISOString()
    };
    
    return Promise.resolve(newTimeline);
  }
};

// Update an existing timeline
export const updateTimeline = async (id: string, timeline: BackingTrack & { TimelineJson?: string }): Promise<BackingTrack> => {
  try {
    // Transform BackingTrack to UpdateTimelineRequest format
    const updateRequest: any = {
      title: timeline.title,
      genre: timeline.genre,
      bpm: timeline.bpm,
      status: timeline.status,
      startOffsetSeconds: timeline.startOffsetSeconds,
      pattern: timeline.pattern,
      detectionConfidence: timeline.detectionConfidence,
      supportedScales: timeline.supportedScales?.map(scale => ({
        key: scale.key,
        scaleType: scale.scaleType,
        confidence: scale.confidence
      }))
    };
    
    // Include TimelineJson if provided (for direct JSON editing)
    if (timeline.TimelineJson !== undefined) {
      updateRequest.TimelineJson = timeline.TimelineJson;
      console.log('API Client: Including TimelineJson in request, length:', timeline.TimelineJson?.length || 0);
    }
    
    const response = await apiClient.put(`/api/timeline/${id}`, updateRequest);
    return response.data;
  } catch (error) {
    console.warn('API not available, returning updated mock timeline for ID:', id);
    return Promise.resolve(timeline);
  }
};

// Delete a timeline
export const deleteTimeline = async (id: string): Promise<void> => {
  try {
    await apiClient.delete(`/api/timeline/${id}`);
  } catch (error) {
    console.warn('API not available, mock deleting timeline ID:', id);
    return Promise.resolve();
  }
};

// Bulk delete timelines
export const deleteMultipleTimelines = async (ids: string[]): Promise<void> => {
  try {
    await apiClient.post('/api/timeline/bulk-delete', { ids });
  } catch (error) {
    console.error('Error bulk deleting timelines:', error);
    throw new Error('Failed to delete timelines');
  }
};

// Regenerate timeline (keep metadata, regenerate timeline entries)
export const regenerateTimeline = async (id: string, options?: {
  pattern?: string;
  startOffsetSeconds?: number;
  expectedChords?: string[];
}): Promise<BackingTrack> => {
  try {
    const response = await apiClient.post(`/api/timeline/${id}/regenerate`, options);
    return response.data;
  } catch (error) {
    console.error('Error regenerating timeline:', error);
    throw new Error('Failed to regenerate timeline');
  }
};

// Get timeline statistics
export interface TimelineStats {
  total: number;
  byStatus: Record<string, number>;
  byGenre: Record<string, number>;
  averageLength: number;
  averageBpm?: number;
  totalDuration: number;
}

export const getTimelineStats = async (): Promise<TimelineStats> => {
  try {
    const response = await apiClient.get('/api/timeline/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching timeline stats:', error);
    throw new Error('Failed to fetch timeline statistics');
  }
};

// Search/filter timelines
export interface TimelineSearchParams {
  query?: string;
  status?: string;
  genre?: string;
  minBpm?: number;
  maxBpm?: number;
  minDuration?: number;
  maxDuration?: number;
  sortBy?: 'createdAt' | 'title' | 'bpm' | 'duration' | 'status';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface TimelineSearchResult {
  timelines: BackingTrack[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const searchTimelines = async (params: TimelineSearchParams): Promise<TimelineSearchResult> => {
  try {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const response = await apiClient.get(`/api/timeline/search?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error searching timelines:', error);
    throw new Error('Failed to search timelines');
  }
};

// Validate YouTube URL and extract video info
export interface YouTubeVideoInfo {
  videoId: string;
  title: string;
  duration: number;
  description?: string;
  channelTitle?: string;
  publishedAt?: string;
  viewCount?: number;
}

export const validateYouTubeUrl = async (url: string): Promise<YouTubeVideoInfo> => {
  try {
    const response = await apiClient.post('/api/timeline/validate-youtube', { url });
    return response.data;
  } catch (error) {
    console.error('Error validating YouTube URL:', error);
    throw new Error('Failed to validate YouTube URL');
  }
};

// Export timeline data
export const exportTimeline = async (id: string, format: 'json' | 'csv' | 'midi'): Promise<Blob> => {
  try {
    const response = await apiClient.get(`/api/timeline/${id}/export/${format}`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting timeline:', error);
    throw new Error('Failed to export timeline');
  }
};

// Import timeline data
export const importTimeline = async (file: File): Promise<BackingTrack> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post('/api/timeline/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error importing timeline:', error);
    throw new Error('Failed to import timeline');
  }
};

// Get timeline processing status
export interface ProcessingStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number; // 0-100
  error?: string;
  startedAt?: string;
  completedAt?: string;
}

export const getProcessingStatus = async (id: string): Promise<ProcessingStatus> => {
  try {
    const response = await apiClient.get(`/api/timeline/${id}/status`);
    return response.data;
  } catch (error) {
    console.error('Error fetching processing status:', error);
    throw new Error('Failed to fetch processing status');
  }
};

// Cancel timeline generation
export const cancelTimelineGeneration = async (id: string): Promise<void> => {
  try {
    await apiClient.post(`/api/timeline/${id}/cancel`);
  } catch (error) {
    console.error('Error cancelling timeline generation:', error);
    throw new Error('Failed to cancel timeline generation');
  }
};